<template>
  <StatsWidget
    :title="$t('stats_dashboard.funnel.title')"
    :description="$t('stats_dashboard.funnel.description')"
    icon="filter"
    tone="violet"
    :loading="widget.loading"
    :error="widget.error"
    :has-data="hasSteps"
    @refresh="$emit('refresh')"
  >
    <ul class="stats-funnel">
      <li
        v-for="step in steps"
        :key="step.key"
        class="stats-funnel__row"
      >
        <div class="stats-funnel__head">
          <span class="stats-funnel__step-label">{{ step.label }}</span>
          <span class="stats-funnel__step-value">{{ step.value }}</span>
        </div>
        <div class="stats-funnel__bar">
          <div
            class="stats-funnel__bar-fill"
            :style="{ width: step.barWidth + '%' }"
          />
        </div>
        <div class="stats-funnel__meta">
          <span v-if="step.conversionLabel" class="stats-funnel__conv">
            {{ $t('stats_dashboard.funnel.conversion_short') }}
            <strong>{{ step.conversionLabel }}</strong>
          </span>
        </div>
      </li>
    </ul>
  </StatsWidget>
</template>

<script>
  /**
   * Funnel widget — 6 steps with horizontal bars sized relative to the
   * top-of-funnel (submissions). Each step also shows its
   * "submissions → step" conversion percentage as documented in §5 of
   * the backend stats guide. We deliberately do NOT recompute step-to-
   * step conversion on the frontend: the backend explicitly normalises
   * everything against submissions for cross-period comparability.
   */
  import { computed, getCurrentInstance } from 'vue';
  import StatsWidget from './StatsWidget.vue';
  import { formatInteger, formatPercent } from '@/utils/statsFormat';

  const STEP_DEFS = [
    {
      key: 'submissions',
      labelKey: 'submissions',
      conversionKey: null, // Top of funnel — no conversion
    },
    {
      key: 'deals',
      labelKey: 'deals',
      conversionKey: 'submissions_to_deals',
    },
    {
      key: 'proposals_sent',
      labelKey: 'proposals_sent',
      conversionKey: 'submissions_to_proposals_sent',
    },
    {
      key: 'proposals_accepted',
      labelKey: 'proposals_accepted',
      conversionKey: 'submissions_to_accepted',
    },
    {
      key: 'invoices_created',
      labelKey: 'invoices_created',
      conversionKey: 'submissions_to_invoiced',
    },
    {
      key: 'invoices_paid',
      labelKey: 'invoices_paid',
      conversionKey: 'submissions_to_paid',
    },
  ];

  export default {
    name: 'StatsFunnel',

    components: { StatsWidget },

    props: {
      widget: { type: Object, required: true },
    },

    emits: ['refresh'],

    setup(props) {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;

      const stepsData = computed(() => props.widget.data?.steps || {});
      const conversion = computed(() => props.widget.data?.conversion || {});

      const hasSteps = computed(() =>
        Object.values(stepsData.value).some((v) => Number(v) > 0)
      );

      const topValue = computed(() => Number(stepsData.value.submissions) || 0);

      const steps = computed(() =>
        STEP_DEFS.map((def) => {
          const rawValue = Number(stepsData.value[def.key]) || 0;
          const ratio = topValue.value > 0 ? rawValue / topValue.value : 0;
          const convValue = def.conversionKey
            ? conversion.value[def.conversionKey]
            : null;
          return {
            key: def.key,
            label: $t(`stats_dashboard.funnel.steps.${def.labelKey}`),
            value: formatInteger(rawValue),
            // Always reserve a sliver of width so a 0-value bar is
            // still visible as an empty channel — keeps the visual
            // funnel shape recognisable.
            barWidth: Math.max(2, Math.round(ratio * 100)),
            conversionLabel:
              convValue == null ? null : formatPercent(convValue, { fractionDigits: 1 }),
          };
        })
      );

      return { steps, hasSteps };
    },
  };
</script>

<style scoped lang="scss">
  @import '@/assets/scss/base/_mixins.scss';

  .stats-funnel {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.625rem;
    flex: 1;

    &__row {
      display: grid;
      grid-template-columns: 1fr;
      gap: 0.25rem;
    }

    &__head {
      display: flex;
      align-items: baseline;
      justify-content: space-between;
      gap: 0.5rem;
    }

    &__step-label {
      font-size: 0.8125rem;
      font-weight: 500;
      color: var(--slate-700);
      @include ellipsis(1);
    }

    &__step-value {
      font-size: 0.875rem;
      font-weight: 700;
      color: var(--slate-900);
      font-variant-numeric: tabular-nums;
    }

    &__bar {
      height: 10px;
      background: var(--slate-100);
      border-radius: 9999px;
      overflow: hidden;
    }

    &__bar-fill {
      height: 100%;
      // Subtle gradient so the bars don't read as flat blocks.
      background: linear-gradient(90deg, var(--brand-400, #60a5fa), var(--brand-600, #2563eb));
      border-radius: 9999px;
      transition: width 0.5s cubic-bezier(0.22, 1, 0.36, 1);
    }

    &__meta {
      display: flex;
      justify-content: flex-end;
      min-height: 14px;
    }

    &__conv {
      font-size: 0.6875rem;
      color: var(--slate-500);
      font-variant-numeric: tabular-nums;

      strong {
        color: var(--slate-700);
        font-weight: 600;
      }
    }
  }
</style>
